import React, { useEffect, useState, useCallback } from 'react';
import TokenManager from '../components/TokenManager';
import TokenPurchase from '../components/TokenPurchase';
import TokenPacks from '../components/TokenPacks';
import DownloadPurchase from '../components/DownloadPurchase';
import { BillingHistory } from '../components/TokenManager';
import { useAuth0 } from '@auth0/auth0-react';
import { fetchTokenBalance, fetchTokenAllowance } from '../utils/tokenUtils';
import { getAuthHeaders } from '../utils/auth';
import { apiFetch } from '../utils/apiUtils';

const TokensPage = ({ 
  tokenBalance, 
  onTokenBalanceChange,
  monthlyAllowance,
  isFounder = false,
  monthlyUsage = 0
}) => {
  const { isAuthenticated: authState, user, getAccessTokenSilently } = useAuth0();
  const [isLoadingBalance, setIsLoadingBalance] = useState(tokenBalance === 0);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [isLoadingSubscription, setIsLoadingSubscription] = useState(true);
  const [actualMonthlyUsage, setActualMonthlyUsage] = useState(monthlyUsage);
  const [isLoadingUsage, setIsLoadingUsage] = useState(true);
  const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState(false);

  // Update loading state when tokenBalance prop changes
  useEffect(() => {
    if (tokenBalance > 0) {
      setIsLoadingBalance(false);
    }
  }, [tokenBalance]);

  // Handle initial load state for unauthenticated users
  useEffect(() => {
    if (!authState) {
      setHasInitiallyLoaded(true);
      setIsLoadingUsage(false);
    } else {
      // Reset when user logs in
      setHasInitiallyLoaded(false);
      setIsLoadingUsage(true);
    }
  }, [authState]);

  // Fetch subscription status
  useEffect(() => {
    const fetchSubscriptionStatus = async () => {
      if (authState && !isFounder) {
        try {
          const token = await getAccessTokenSilently();
          const response = await fetch(`${process.env.REACT_APP_API_URL}/api/tokens/subscription`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          
          if (response.ok) {
            const data = await response.json();
            setSubscriptionStatus(data);
    
          } else {
            console.error('[RETRO] ❌ Failed to fetch subscription status');
            setSubscriptionStatus({ hasActiveSubscription: false });
          }
        } catch (error) {
          console.error('[RETRO] ❌ Error fetching subscription status:', error);
          setSubscriptionStatus({ hasActiveSubscription: false });
        }
      } else {
        // Founders don't need subscription checks
        setSubscriptionStatus({ hasActiveSubscription: false });
      }
      setIsLoadingSubscription(false);
    };

    if (authState) {
      fetchSubscriptionStatus();
    }
  }, [authState, getAccessTokenSilently, isFounder]);

  // Fetch actual monthly usage with proper billing cycle calculation
  const fetchMonthlyUsage = useCallback(async (isInitialLoad = false) => {
    if (!authState || !user) {
      if (isInitialLoad) {
        setIsLoadingUsage(false);
        setHasInitiallyLoaded(true);
      }
      return;
    }
    
    try {
      // Only show loading state for initial load, not background refreshes
      if (isInitialLoad) {
        setIsLoadingUsage(true);
      }
      
      const headers = await getAuthHeaders(getAccessTokenSilently);

      const data = await apiFetch(`/api/tokens/monthly-usage`, {
        headers,
        credentials: 'include',
        mode: 'cors'
      });
      
      setActualMonthlyUsage(data.monthlyUsage || 0);
      
      console.log('[RETRO-TOKENS] 📊 Monthly usage calculated:', {
        monthlyUsage: data.monthlyUsage,
        billingPeriod: data.billingCycle?.isCalendarMonth ? 'calendar' : 'billing_cycle',
        periodStart: data.billingCycle?.periodStart,
        periodEnd: data.billingCycle?.periodEnd,
        userEmail: user?.email,
        isInitialLoad,
        timestamp: new Date().toISOString()
      });
      
    } catch (error) {
      console.error('[RETRO-TOKENS] ❌ Failed to fetch monthly usage:', error);
      setActualMonthlyUsage(monthlyUsage); // Fall back to prop value
    } finally {
      if (isInitialLoad) {
        setIsLoadingUsage(false);
        setHasInitiallyLoaded(true);
      }
    }
  }, [authState, getAccessTokenSilently, user, monthlyUsage]);

  // Fetch monthly usage when component mounts
  useEffect(() => {
    fetchMonthlyUsage(true); // Initial load
  }, [fetchMonthlyUsage]);

  // Refresh user images when token balance changes (indicating new image generation)
  useEffect(() => {
    if (authState && tokenBalance > 0 && hasInitiallyLoaded) {
      // Small delay to ensure the backend has processed the new image
      const timer = setTimeout(() => {
        fetchMonthlyUsage(false); // Background refresh - don't show loading
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [tokenBalance, authState, fetchMonthlyUsage, hasInitiallyLoaded]);

  // Refresh token balance when component mounts
  useEffect(() => {
    const refreshTokenBalance = async () => {
      if (authState && onTokenBalanceChange) {
        try {
          setIsLoadingBalance(true);
          
          // Fetch balance and update immediately
          const balance = await fetchTokenBalance(getAccessTokenSilently);
          onTokenBalanceChange(balance);
          setIsLoadingBalance(false);
          
          // Fetch allowance separately (non-blocking)
          fetchTokenAllowance(getAccessTokenSilently).catch(() => {
            // Allowance fetch failure is non-critical
          });
          
        } catch (error) {
          console.error('Failed to refresh token balance:', error);
          setIsLoadingBalance(false);
        }
      }
    };

    // Small delay to ensure Auth0 is ready
    const timer = setTimeout(refreshTokenBalance, 100);
    return () => clearTimeout(timer);
  }, [authState, getAccessTokenSilently, onTokenBalanceChange]);



  // Get the first day of the current month
  const resetDate = new Date();
  resetDate.setDate(1);
  resetDate.setHours(0, 0, 0, 0);

  // Comprehensive loading state - wait for all critical data to load
  // For unauthenticated users, we're "loaded" since we don't need data
  // For founders, we don't need to wait for subscription status
  const isFullyLoaded = !authState || (!isLoadingBalance && 
    (isFounder ? hasInitiallyLoaded : (!isLoadingSubscription && hasInitiallyLoaded)));

  console.log('[RETRO-FOUNDER] TokensPage props:', {
    isFounder,
    monthlyUsage,
    userEmail: user?.email,
    isAuthenticated: authState,
    hasActiveSubscription: subscriptionStatus?.hasActiveSubscription,
    isLoadingBalance,
    isLoadingSubscription,
    isLoadingUsage,
    hasInitiallyLoaded,
    isFullyLoaded,
    timestamp: new Date().toISOString()
  });

  return (
    <div className="max-w-[920px] mx-auto">
      <div className="space-y-8 mb-8">
        <TokenManager 
          tokenBalance={tokenBalance} 
          onTokenBalanceChange={onTokenBalanceChange}
          resetDate={resetDate}
          monthlyAllowance={monthlyAllowance}
          isFounder={isFounder}
          monthlyUsage={actualMonthlyUsage}
          isLoadingBalance={!isFullyLoaded}
        />
        {!isFounder && (
          <>
            <TokenPurchase 
              tokenBalance={tokenBalance} 
              onTokenBalanceChange={onTokenBalanceChange}
              currentPlan={subscriptionStatus?.subscriptionTier}
              isLoading={isLoadingSubscription}
            />
            {/* Only show topups if user has an active subscription */}
            {subscriptionStatus?.hasActiveSubscription && (
              <TokenPacks 
                tokenBalance={tokenBalance} 
                onTokenBalanceChange={onTokenBalanceChange}
              />
            )}
            <DownloadPurchase 
              tokenBalance={tokenBalance} 
              onTokenBalanceChange={onTokenBalanceChange}
            />
          </>
        )}
        
        {/* Purchase History Section - Only show for non-founders */}
        {!isFounder && (
          <div className="bg-white/60 rounded-2xl p-8">
            <h2 className="text-2xl font-['VT323'] text-black/80 mb-6 text-center">💳 Purchase History</h2>
            <BillingHistory 
              isAuthenticated={authState}
              onLogin={() => window.location.href = '/login'}
              isFounder={isFounder}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default TokensPage; 